import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '@/contexts/NotificationContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Bell, X, BellRing } from 'lucide-react';

const NotificationPrompt = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const { isAuthenticated, user } = useAuth();
  const { permission, isSupported, requestPermission } = useNotifications();
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Check if prompt was previously dismissed
    const dismissed = localStorage.getItem('notification_prompt_dismissed');
    if (dismissed) {
      setIsDismissed(true);
    }

    // Show prompt only for authenticated users who haven't enabled notifications
    // and haven't dismissed the prompt
    if (
      isAuthenticated && 
      isSupported && 
      permission === 'default' && 
      !dismissed
    ) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [isAuthenticated, isSupported, permission]);

  const handleEnable = async () => {
    const result = await requestPermission();
    if (result.success) {
      setIsVisible(false);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    localStorage.setItem('notification_prompt_dismissed', 'true');
  };

  const handleRemindLater = () => {
    setIsVisible(false);
    // Will show again on next page load
  };

  if (!isVisible || !isAuthenticated || !isSupported || permission !== 'default') {
    return null;
  }

  const isDoctor = user?.role === 'doctor' || user?.type === 'doctor';

  return (
    <div 
      className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-4 duration-300"
      data-testid="notification-prompt"
    >
      <Card className="w-80 shadow-lg border-teal-200 dark:border-teal-800 bg-white dark:bg-slate-800">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 p-2 bg-teal-100 dark:bg-teal-900/30 rounded-full">
              <BellRing className="h-6 w-6 text-teal-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-slate-900 dark:text-white">
                  {lang === 'ar' ? 'تفعيل الإشعارات' : 'Enable Notifications'}
                </h4>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 -mr-2 -mt-1"
                  onClick={handleDismiss}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                {isDoctor 
                  ? (lang === 'ar' 
                      ? 'احصل على إشعارات فورية عند حجز موعد جديد أو إلغائه أو استلام رسالة من المريض'
                      : 'Get instant notifications for new appointments, cancellations, and patient messages')
                  : (lang === 'ar'
                      ? 'احصل على تذكيرات المواعيد ورسائل الأطباء والتحديثات المهمة'
                      : 'Get appointment reminders, doctor messages, and important updates')}
              </p>
              <div className="flex items-center gap-2 mt-3">
                <Button
                  size="sm"
                  className="flex-1 bg-teal-600 hover:bg-teal-700"
                  onClick={handleEnable}
                >
                  <Bell className="h-4 w-4 mr-1" />
                  {lang === 'ar' ? 'تفعيل' : 'Enable'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleRemindLater}
                >
                  {lang === 'ar' ? 'لاحقاً' : 'Later'}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationPrompt;
